import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { BillingView, PlanAddonPanel } from '@copilot/ui';
import Loader from '../components/common/Loader';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { usePlanAddonCheckout } from '../hooks/usePlanAddonCheckout';
import { ANALYTICS_EVENTS, track } from '../services/analytics';
import * as billingApi from '../services/billingApi';
import * as planAddonApi from '../services/planAddonApi';
import { reportError } from '../services/errorTracking';
import * as pricingApi from '../services/pricingApi';
import { getUserFriendlyError } from '../utils/errors';

// Plan & Billing - the shared view (@copilot/ui's BillingView), loaded
// through Web's API client. Razorpay offers no hosted customer portal,
// so cancellation and payment history are built here, backed by the
// backend's own `payments` table rather than a live provider call.

// Matches the backend default (backend/routers/billing.py's `limit`).
const PAYMENT_HISTORY_PAGE_SIZE = 50;
const RECEIPT_FILENAME_FALLBACK = 'payment-receipt.pdf';

function safeReceiptFilename(filename) {
  return /^payment-receipt-[A-Za-z0-9-]+\.pdf$/.test(filename || '')
    ? filename
    : RECEIPT_FILENAME_FALLBACK;
}

function BillingPage() {
  const [usage, setUsage] = useState(null);
  const [billing, setBilling] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsOffset, setPaymentsOffset] = useState(0);
  const [paymentsError, setPaymentsError] = useState('');
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // The server's entitlement (eligibility, plan rate, extra balance) for
  // the extra-meetings panel. Best-effort: without it the panel is simply
  // not shown.
  const [entitlement, setEntitlement] = useState(null);

  const loadEntitlement = useCallback(async () => {
    try {
      setEntitlement(await billingApi.getEntitlement());
    } catch (_) {
      // The panel is simply not shown.
    }
  }, []);

  // Its own loader so paging and the history-only retry re-fetch just
  // this section. A failed request is never shown as "no payments".
  const loadPayments = useCallback(async (offset = 0) => {
    setPaymentsLoading(true);
    setPaymentsError('');

    try {
      const history = await billingApi.getPaymentHistory({
        limit: PAYMENT_HISTORY_PAGE_SIZE,
        offset,
      });
      setPayments(history.items || []);
      setPaymentsTotal(history.total ?? (history.items || []).length);
      setPaymentsOffset(offset);
    } catch (err) {
      setPaymentsError(err.message || 'Unable to load payment history.');
    } finally {
      setPaymentsLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const [summary, overview] = await Promise.all([
        pricingApi.getMySubscription(),
        billingApi.getMyBilling(),
      ]);
      setUsage(summary);
      setBilling(overview);

      // Best-effort: a pending plan-change request must never hide the
      // plan details if it fails to load.
      try {
        setPendingRequest(await pricingApi.getMyPendingRequest());
      } catch (_) {
        setPendingRequest(null);
      }

      // The payments ledger stays readable when the provider is disabled
      // or the plan is free; a failure stays isolated to the history.
      await loadPayments(0);
    } catch (err) {
      setError(err.message || 'Unable to load billing details.');
    } finally {
      setIsLoading(false);
    }
  }, [loadPayments]);

  useEffect(() => {
    load();
    loadEntitlement();
  }, [load, loadEntitlement]);

  const addonCheckout = usePlanAddonCheckout({
    onPurchased: () => {
      // The balance, the allowance and the history all moved.
      loadEntitlement();
      loadPayments(0);
    },
  });

  const buyExtraMeetings = (quantity) => addonCheckout.start(quantity);

  // Silent re-read of the same data, current payments page included.
  const refreshBilling = useCallback(
    async ({ isStale }) => {
      const [summary, overview] = await Promise.all([
        pricingApi.getMySubscription(),
        billingApi.getMyBilling(),
      ]);
      const [pending, history] = await Promise.all([
        pricingApi.getMyPendingRequest().then(
          (value) => ({ value }),
          () => null
        ),
        billingApi
          .getPaymentHistory({ limit: PAYMENT_HISTORY_PAGE_SIZE, offset: paymentsOffset })
          .catch(() => null),
      ]);

      // Extra meetings whose payment was confirmed late (webhook or the
      // recovery sweep), or used up by a meeting started elsewhere.
      const latestEntitlement = await billingApi.getEntitlement().catch(() => null);

      if (isStale()) return;
      if (latestEntitlement) setEntitlement(latestEntitlement);
      setUsage(summary);
      setBilling(overview);
      if (pending) setPendingRequest(pending.value);
      if (history) {
        setPayments(history.items || []);
        setPaymentsTotal(history.total ?? (history.items || []).length);
      }
    },
    [paymentsOffset]
  );

  useAutoRefresh(refreshBilling, { enabled: !isLoading && !paymentsLoading });

  const downloadReceipt = async (paymentId) => {
    try {
      const { blob, filename } = await billingApi.downloadPaymentReceipt(paymentId);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = safeReceiptFilename(filename);
      try {
        link.click();
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    } catch (err) {
      reportError('payment_receipt_download', err);
      throw err;
    }
  };

  return (
    <>
    <PlanAddonPanel
      entitlement={entitlement}
      getQuote={planAddonApi.getQuote}
      onBuy={buyExtraMeetings}
      busy={addonCheckout.isBusy}
      addedQuantity={addonCheckout.addedQuantity}
      notice={
        addonCheckout.stage ? (
          <div className={`form-alert billing-alert-notice checkout-stage-${addonCheckout.stage.stage}`} role="status">
            {addonCheckout.stage.message}
          </div>
        ) : null
      }
    />
    <BillingView
      isLoading={isLoading}
      error={error}
      onRetry={load}
      loadingIndicator={<Loader label="Loading billing..." />}
      usage={usage}
      billing={billing}
      pendingRequest={pendingRequest}
      payments={{
        items: payments,
        error: paymentsError,
        loading: paymentsLoading,
        onRetry: () => loadPayments(paymentsOffset),
        offset: paymentsOffset,
        total: paymentsTotal,
        pageSize: PAYMENT_HISTORY_PAGE_SIZE,
        onPage: (offset) => loadPayments(offset),
      }}
      plansHref="/app/billing/plans"
      renderLink={({ href, className, children }) => (
        <Link to={href} className={className}>
          {children}
        </Link>
      )}
      onCancelSubscription={async () => {
        await billingApi.cancelSubscription();
        track(ANALYTICS_EVENTS.SUBSCRIPTION_CANCELLED);
        await load();
      }}
      onDownloadReceipt={downloadReceipt}
      describeError={getUserFriendlyError}
      // The server decides; only changes the out-of-meetings wording.
      addonCheckoutAvailable={Boolean(entitlement?.addon_checkout_available)}
    />
    </>
  );
}

export default BillingPage;
